import React, { useState, useEffect } from 'react';
import { generateFieldReferencesList } from '../utils/flowUtils';
import { useCollapsibleState } from '../hooks/useCollapsibleState';
import { supabase } from '../services/supabaseClient';

interface FlowFieldReferencesProps {
  flow: any;
}

interface FieldReference {
  id: string;
  object_name: string;
  field_name: string;
  relationship_type: string;
  element_name: string;
  reference_type: string;
}

const FlowFieldReferences: React.FC<FlowFieldReferencesProps> = ({ flow }) => {
  const [isExpanded, setIsExpanded] = useCollapsibleState('flowFieldReferences', false);
  const [fieldReferences, setFieldReferences] = useState<FieldReference[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Fetch field references from database
  useEffect(() => {
    const fetchFieldReferences = async () => {
      if (!flow?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('sf_objects')
          .select('*')
          .eq('flow_id', flow.id)
          .order('object_name', { ascending: true })
          .order('field_name', { ascending: true });
          
        if (error) {
          console.error('Error fetching field references:', error);
        } else {
          setFieldReferences(data || []);
        }
      } catch (error) {
        console.error('Error fetching field references:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchFieldReferences();
  }, [flow?.id]);
  
  // Group by object name for better organization
  const groupedReferences: { [key: string]: FieldReference[] } = {};
  
  fieldReferences.forEach(ref => {
    if (!groupedReferences[ref.object_name]) {
      groupedReferences[ref.object_name] = [];
    }
    groupedReferences[ref.object_name].push(ref);
  });
  
  // Get unique object names
  const objectNames = Object.keys(groupedReferences).sort();
  const hasData = objectNames.length > 0;
  
  return (
    <div className="bg-white shadow overflow-hidden sm:rounded-lg mb-6">
      <div className="px-4 py-5 sm:px-6 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg leading-6 font-medium text-gray-900">Fields Used in Flow</h3>
            <p className="mt-1 max-w-2xl text-sm text-gray-500">
              Objects and fields referenced by this flow.
            </p>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? '' : 'rotate-180'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      
      {isExpanded && (
        <div className="border-t border-gray-200 py-2">
        {loading ? (
          <div className="px-4 py-3 text-center italic text-gray-500">
            Loading field references...
          </div>
        ) : hasData ? (
          <div className="px-4 py-5 sm:p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Object</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usage</th>
                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Element</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {objectNames.map((objectName) => {
                    const refs = groupedReferences[objectName];
                    return refs.map((reference, index) => {
                      const isFirstInGroup = index === 0;
                      const rowIndex = objectNames.indexOf(objectName) + index;
                      
                      // Define usage type colors
                      const usageColors: { [key: string]: string } = {
                        'condition': 'bg-yellow-100 text-yellow-800',
                        'update': 'bg-green-100 text-green-800',
                        'create': 'bg-blue-100 text-blue-800',
                        'delete': 'bg-red-100 text-red-800',
                        'read': 'bg-purple-100 text-purple-800',
                        'reference': 'bg-gray-100 text-gray-800'
                      };
                      
                      const usageColor = usageColors[reference.relationship_type] || usageColors['reference'];
                      
                      return (
                        <tr key={reference.id} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          {isFirstInGroup ? (
                            <td rowSpan={refs.length} className="px-3 py-2 text-sm font-medium text-gray-900 align-top">
                              {objectName}
                            </td>
                          ) : null}
                          <td className="px-3 py-2 text-sm text-gray-500">{reference.field_name}</td>
                          <td className="px-3 py-2 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${usageColor}`}>
                              {reference.relationship_type}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-sm text-gray-500">{reference.element_name}</td>
                        </tr>
                      );
                    });
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="px-4 py-3 text-center italic text-gray-500">
            No field references found for this flow.
          </div>
        )}
        </div>
      )}
    </div>
  );
};

export default FlowFieldReferences;